import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import RepairTabs, { type RepairTab } from '../components/RepairTabs';
import { fetchPageContent, type PageSection } from '../../../utils/pageContentStorage';

const TABS: RepairTab[] = [
  {
    key: 'screen',
    label: '螢幕更換',
    image: 'https://storage.readdy-site.link/project_files/a6fca87a-8436-4bd7-9e14-084f599afe41/14751cc4-1539-4b6b-90f9-341d088de198_SnapInsta.to_573667018_18037605671686948_4733626729554609701_n.jpg?v=fe5baff1ba4399e6b4ec7a5b5fb6a964',
    objectPosition: 'center',
    priceFrom: 'NT$12,000 起',
    time: '2–4 小時',
    warranty: '3 個月',
    features: [
      { point: 'Retina / ProMotion 面板', detail: '使用符合原廠規格的高解析面板，維持色域與亮度表現。' },
      { point: '黑屏、破屏、閃屏均可處理', detail: '背光失效、LCD 破損、顯示異常均可評估維修。' },
      { point: '更換後色彩校正', detail: '確認色彩準確，無偏色、壞點問題後交件。' },
    ],
  },
  {
    key: 'keyboard',
    label: '鍵盤更換',
    image: 'https://storage.readdy-site.link/project_files/a6fca87a-8436-4bd7-9e14-084f599afe41/e4a992af-5dd2-46c5-a969-8a7c86ea392f_-2.jpg?v=cb43a67a0a2eeb44efa1f22b4f56894b',
    objectPosition: 'center',
    priceFrom: 'NT$4,000 起',
    time: '2–3 小時',
    warranty: '3 個月',
    features: [
      { point: '全鍵盤組更換', detail: '蝶式鍵盤、剪刀腳鍵盤故障均可處理，含 Touch Bar 問題。' },
      { point: '鍵帽鬆脫須整組更換', detail: '鍵帽脫落或鍵盤內部故障皆需更換整組鍵盤，無法單鍵處理。' },
      { point: 'Backlight 背光維修', detail: '按鍵沒有背光或背光不均勻，可同步一併處理。' },
    ],
  },
  {
    key: 'water',
    label: '進水搶救',
    image: 'https://storage.readdy-site.link/project_files/a6fca87a-8436-4bd7-9e14-084f599afe41/2a4c5dc9-7bf2-441f-a909-8aee6dafef3a_SnapInsta.to_572671533_18037605605686948_4498365020344352633_n.jpg?v=6c2b7b2b89c17ebd0f0f39ea3e92f7a5',
    objectPosition: 'center',
    priceFrom: 'NT$3,000 起',
    time: '1-7 工作天',
    warranty: '3 個月',
    features: [
      { point: '立即關機是關鍵', detail: '進水後請立即關機，不要嘗試充電或開機，越快送修救回率越高。' },
      { point: '超音波清洗 + 腐蝕處理', detail: '徹底清洗主板腐蝕物，提升長期穩定性。' },
      { point: '主板短路與 IC 檢測', detail: '進水後的短路位置逐一排查，不留隱患。' },
    ],
  },
  {
    key: 'board',
    label: '主板維修',
    image: 'https://storage.readdy-site.link/project_files/a6fca87a-8436-4bd7-9e14-084f599afe41/8649c210-5618-4100-a865-50727238f375_SnapInsta.to_524161738_18025663508686948_3461203035808973103_n.jpg?v=03e0c06145718abf77fdc4283b80d3fb',
    objectPosition: 'center',
    priceFrom: 'NT$5,000 起',
    time: '1-5 工作天',
    warranty: '3 個月',
    features: [
      { point: 'M 系列晶片主板維修', detail: 'M1/M2/M3/M4 主板均可評估，配備顯微焊接與熱風設備。' },
      { point: '無法開機、不充電、黑屏', detail: '各類主板症狀逐步排查，找出根本原因。' },
      { point: '承接同業代工', detail: '其他維修店無法處理的高難度案件，可轉介本店評估。' },
    ],
  },
  {
    key: 'ssd',
    label: 'SSD / 硬碟',
    image: 'https://storage.readdy-site.link/project_files/a6fca87a-8436-4bd7-9e14-084f599afe41/7c524d4b-0f7c-4dad-a1ed-1301774fea91_unnamed.jpg?v=87e561ed5c301a6f82e326f15d607a89',
    objectPosition: 'center',
    priceFrom: 'NT$3,500 起',
    time: '當天可好',
    warranty: '3 個月',
    features: [
      { point: 'SSD 更換 / 容量升級', detail: '原廠 SSD 損壞或容量不足，可依需求更換更大容量。' },
      { point: '資料搶救評估', detail: 'SSD 損壞導致資料遺失，可評估資料救援可行性。' },
      { point: '系統遷移協助', detail: '更換後可協助重新安裝 macOS，資料移轉完整。' },
    ],
  },
];

export default function MacServicePage() {
  const navigate = useNavigate();
  const [bannerData, setBannerData] = useState<PageSection | null>(null);

  useEffect(() => {
    fetchPageContent().then(content => {
      setBannerData(content.find(s => s.id === 'mac-banner') ?? null);
    });
  }, []);

  const bannerTitle = bannerData?.title ?? 'Mac 維修';
  const bannerSubtitle = bannerData?.subtitle ?? 'MacBook Air / Pro 全系列，含 M 系列晶片';
  const bannerImage = bannerData?.image ?? 'https://ollumiosiejdxqaygglt.supabase.co/storage/v1/object/public/news-images/site-images/machero.jpg';

  const models = ['MacBook Air 13" M1/M2/M3/M4', 'MacBook Air 15" M2/M3/M4', 'MacBook Pro 14" M2/M3/M4', 'MacBook Pro 16" M2/M3/M4', 'MacBook Pro 13" (Intel/M系列)', 'MacBook Air (Intel 系列)'];

  const faqs = [
    { q: 'MacBook 進水應該怎麼辦？', a: '立刻關機，不要嘗試充電或開機！翻轉讓水流出後，盡快帶機到店。越快送修，主板受損程度越低，救回率越高。' },
    { q: 'M 系列 MacBook 也可以修嗎？', a: '可以。我們具備 M 系列晶片主板的顯微焊接能力，但部分零件較難取得，維修前會說明時程。' },
    { q: 'MacBook 鍵盤壞掉一定要換整片嗎？', a: '是的，無論是鍵帽脫落還是蝶式鍵盤內部故障，我們均採整組鍵盤更換方式處理，不提供單鍵維修服務。帶機到店診斷後即可確認更換方案。' },
    { q: 'MacBook 換 SSD 資料會遺失嗎？', a: '更換前可先備份，或由我們協助資料遷移。如原 SSD 仍可讀取，資料通常可完整搬移到新 SSD。' },
  ];

  return (
    <>
      <Navbar />
      <main>
        <section className="pt-32 pb-16 bg-gray-950 relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <img src={bannerImage} alt="" className="w-full h-full object-cover object-top" />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6">
            <div className="flex items-center gap-3 mb-6">
              <button onClick={() => navigate('/services')} className="text-gray-500 hover:text-orange-400 text-sm cursor-pointer">維修項目</button>
              <i className="ri-arrow-right-s-line text-gray-600" />
              <span className="text-orange-400 text-sm">Mac 維修</span>
            </div>
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 flex items-center justify-center bg-orange-500/20 border border-orange-500/30 rounded-2xl">
                <i className="ri-macbook-line text-orange-400 text-3xl" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-white">{bannerTitle}</h1>
                <p className="text-gray-400 mt-1">{bannerSubtitle}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-3 gap-12">
              <div className="lg:col-span-2">
                <h2 className="text-xl font-bold text-gray-900 mb-6">選擇維修項目</h2>
                <RepairTabs
                  tabs={TABS}
                  note="MacBook 進水請<strong>立即關機</strong>，不要嘗試充電或開機！盡快帶機到店，越早送修救機率越高。"
                  noteType="warning"
                />
              </div>
              <div className="space-y-5">
                <div className="bg-gray-950 rounded-2xl p-6 text-white">
                  <h3 className="font-bold mb-1 text-sm text-orange-400 uppercase tracking-wide">支援機型</h3>
                  <p className="text-gray-400 text-xs mb-4">以下所有系列均支援</p>
                  <ul className="space-y-2">
                    {models.map(m => (
                      <li key={m} className="flex items-center gap-2 text-sm text-gray-300">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-orange-400 text-xs" />
                        </div>
                        {m}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-orange-50 border border-orange-100 rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-orange-500 rounded-xl">
                      <i className="ri-shield-check-line text-white text-sm" />
                    </div>
                    <span className="font-bold text-gray-900 text-sm">3 個月保固</span>
                  </div>
                  <p className="text-gray-500 text-xs leading-relaxed">所有維修項目提供 3 個月保固，因維修部件本身問題免費重修。</p>
                </div>
                <div className="bg-gray-950 rounded-2xl p-5 text-center">
                  <div className="text-white text-sm font-semibold mb-1">免費到店診斷</div>
                  <div className="text-gray-400 text-xs mb-4">MacBook 問題複雜，建議帶機評估</div>
                  <button onClick={() => navigate('/contact')} className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl cursor-pointer whitespace-nowrap transition-colors text-sm">立即預約</button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">常見問題</h2>
            <div className="space-y-4">
              {faqs.map((f, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0 mt-0.5">
                      <span className="text-orange-500 text-xs font-bold">Q</span>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 text-sm mb-2">{f.q}</div>
                      <div className="text-gray-500 text-sm leading-relaxed">{f.a}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 bg-gray-950">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-2xl font-bold text-white mb-3">MacBook 出問題了？交給我們</h2>
            <p className="text-gray-400 text-sm mb-7">免費診斷・透明報價・M 系列主板維修・3 個月保固</p>
            <div className="flex flex-wrap justify-center gap-4">
              <button onClick={() => navigate('/pricing')} className="px-8 py-3.5 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full cursor-pointer whitespace-nowrap transition-colors">查詢維修報價</button>
              <button onClick={() => navigate('/contact')} className="px-8 py-3.5 border border-white/30 text-white hover:bg-white/10 font-semibold rounded-full cursor-pointer whitespace-nowrap transition-colors">立即聯絡</button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
